import Phaser from 'phaser';

import { toPhaserLoaderUrl } from './loaderAssetPath';

export const SUNNYSIDE_TILESET_KEY = 'sunnyside-world-tileset';

const SUNNYSIDE_TILESET_PATH = '/assets/tilesets/sunnyside-world-16px.png';
const HUMAN_BASE_WALK_TEXTURE_KEY = 'sunnyside-human-base-walk';
const HUMAN_BASE_IDLE_TEXTURE_KEY = 'sunnyside-human-base-idle';
const HUMAN_BASE_WALK_ANIMATION_KEY = 'sunnyside-human-base-walk-animation';
const HUMAN_BASE_IDLE_ANIMATION_KEY = 'sunnyside-human-base-idle-animation';
const HUMAN_FRAME_WIDTH = 96;
const HUMAN_FRAME_HEIGHT = 64;
const HUMAN_VISUAL_SCALE = 1.5;

export type HumanAnimationState = 'idle' | 'walk';
export type HumanHairStyle =
  | 'bowlhair'
  | 'curlyhair'
  | 'longhair'
  | 'mophair'
  | 'shorthair'
  | 'spikeyhair';

const HAIR_TEXTURES: Record<HumanHairStyle, { walk: string; idle: string }> = {
  bowlhair: {
    walk: '/assets/characters/sunnyside/human/bowlhair_walk_strip8.png',
    idle: '/assets/characters/sunnyside/human/bowlhair_idle_strip9.png',
  },
  curlyhair: {
    walk: '/assets/characters/sunnyside/human/curlyhair_walk_strip8.png',
    idle: '/assets/characters/sunnyside/human/curlyhair_idle_strip9.png',
  },
  longhair: {
    walk: '/assets/characters/sunnyside/human/longhair_walk_strip8.png',
    idle: '/assets/characters/sunnyside/human/longhair_idle_strip9.png',
  },
  mophair: {
    walk: '/assets/characters/sunnyside/human/mophair_walk_strip8.png',
    idle: '/assets/characters/sunnyside/human/mophair_idle_strip9.png',
  },
  shorthair: {
    walk: '/assets/characters/sunnyside/human/shorthair_walk_strip8.png',
    idle: '/assets/characters/sunnyside/human/shorthair_idle_strip9.png',
  },
  spikeyhair: {
    walk: '/assets/characters/sunnyside/human/spikeyhair_walk_strip8.png',
    idle: '/assets/characters/sunnyside/human/spikeyhair_idle_strip9.png',
  },
};

const HAIR_STYLES = Object.keys(HAIR_TEXTURES) as HumanHairStyle[];

export interface HumanVisual {
  root: Phaser.GameObjects.Container;
  base: Phaser.GameObjects.Sprite;
  hair: Phaser.GameObjects.Sprite;
  state: HumanAnimationState;
  hairStyle: HumanHairStyle;
  scale: number;
}

export interface CreateHumanVisualOptions {
  interactive?: boolean;
  hairStyle?: HumanHairStyle;
  hairColor?: string;
  baseColor?: string;
  scale?: number;
}

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const toTint = (colorHex: string | null): number | null => {
  if (!colorHex) {
    return null;
  }
  const normalized = colorHex.trim();
  if (!/^#[0-9a-f]{6}$/i.test(normalized)) {
    return null;
  }
  return Number(`0x${normalized.slice(1)}`);
};

const blendWithWhite = (tint: number, ratio: number): number => {
  const clampedRatio = Phaser.Math.Clamp(ratio, 0, 1);
  const red = (tint >> 16) & 0xff;
  const green = (tint >> 8) & 0xff;
  const blue = tint & 0xff;
  const blend = (channel: number): number =>
    Math.round(channel * (1 - clampedRatio) + 255 * clampedRatio);

  return (blend(red) << 16) | (blend(green) << 8) | blend(blue);
};

const hairWalkTextureKey = (style: HumanHairStyle): string => `sunnyside-human-hair-${style}-walk`;
const hairIdleTextureKey = (style: HumanHairStyle): string => `sunnyside-human-hair-${style}-idle`;
const hairWalkAnimationKey = (style: HumanHairStyle): string =>
  `sunnyside-human-hair-${style}-walk-animation`;
const hairIdleAnimationKey = (style: HumanHairStyle): string =>
  `sunnyside-human-hair-${style}-idle-animation`;

const getBaseAnimationKey = (state: HumanAnimationState): string =>
  state === 'walk' ? HUMAN_BASE_WALK_ANIMATION_KEY : HUMAN_BASE_IDLE_ANIMATION_KEY;

const getHairAnimationKey = (style: HumanHairStyle, state: HumanAnimationState): string =>
  state === 'walk' ? hairWalkAnimationKey(style) : hairIdleAnimationKey(style);

const getHairTextureKey = (style: HumanHairStyle, state: HumanAnimationState): string =>
  state === 'walk' ? hairWalkTextureKey(style) : hairIdleTextureKey(style);

export const pickHumanHairStyle = (seed: string): HumanHairStyle =>
  HAIR_STYLES[hashString(seed) % HAIR_STYLES.length] ?? 'shorthair';

export const resolveHairStyleFromSpriteId = (
  spriteId: string,
  fallbackSeed: string,
): HumanHairStyle => {
  const normalized = spriteId.trim().toLowerCase();
  if (normalized.endsWith('01')) return 'shorthair';
  if (normalized.endsWith('02')) return 'curlyhair';
  if (normalized.endsWith('03')) return 'longhair';
  if (normalized.endsWith('04')) return 'spikeyhair';
  if (normalized.endsWith('05')) return 'bowlhair';
  if (normalized.endsWith('06')) return 'mophair';
  return pickHumanHairStyle(fallbackSeed);
};

export const preloadSunnysideAssets = (scene: Phaser.Scene): void => {
  scene.load.image(SUNNYSIDE_TILESET_KEY, toPhaserLoaderUrl(SUNNYSIDE_TILESET_PATH));
  scene.load.spritesheet(
    HUMAN_BASE_WALK_TEXTURE_KEY,
    toPhaserLoaderUrl('/assets/characters/sunnyside/human/base_walk_strip8.png'),
    { frameWidth: HUMAN_FRAME_WIDTH, frameHeight: HUMAN_FRAME_HEIGHT },
  );
  scene.load.spritesheet(
    HUMAN_BASE_IDLE_TEXTURE_KEY,
    toPhaserLoaderUrl('/assets/characters/sunnyside/human/base_idle_strip9.png'),
    { frameWidth: HUMAN_FRAME_WIDTH, frameHeight: HUMAN_FRAME_HEIGHT },
  );

  HAIR_STYLES.forEach((style) => {
    scene.load.spritesheet(
      hairWalkTextureKey(style),
      toPhaserLoaderUrl(HAIR_TEXTURES[style].walk),
      {
        frameWidth: HUMAN_FRAME_WIDTH,
        frameHeight: HUMAN_FRAME_HEIGHT,
      },
    );
    scene.load.spritesheet(
      hairIdleTextureKey(style),
      toPhaserLoaderUrl(HAIR_TEXTURES[style].idle),
      {
        frameWidth: HUMAN_FRAME_WIDTH,
        frameHeight: HUMAN_FRAME_HEIGHT,
      },
    );
  });
};

const ensureHumanAnimations = (scene: Phaser.Scene): void => {
  if (!scene.anims.exists(HUMAN_BASE_WALK_ANIMATION_KEY)) {
    scene.anims.create({
      key: HUMAN_BASE_WALK_ANIMATION_KEY,
      frames: scene.anims.generateFrameNumbers(HUMAN_BASE_WALK_TEXTURE_KEY, { start: 0, end: 7 }),
      frameRate: 10,
      repeat: -1,
    });
  }

  if (!scene.anims.exists(HUMAN_BASE_IDLE_ANIMATION_KEY)) {
    scene.anims.create({
      key: HUMAN_BASE_IDLE_ANIMATION_KEY,
      frames: scene.anims.generateFrameNumbers(HUMAN_BASE_IDLE_TEXTURE_KEY, { start: 0, end: 8 }),
      frameRate: 8,
      repeat: -1,
    });
  }

  HAIR_STYLES.forEach((style) => {
    if (!scene.anims.exists(hairWalkAnimationKey(style))) {
      scene.anims.create({
        key: hairWalkAnimationKey(style),
        frames: scene.anims.generateFrameNumbers(hairWalkTextureKey(style), { start: 0, end: 7 }),
        frameRate: 10,
        repeat: -1,
      });
    }

    if (!scene.anims.exists(hairIdleAnimationKey(style))) {
      scene.anims.create({
        key: hairIdleAnimationKey(style),
        frames: scene.anims.generateFrameNumbers(hairIdleTextureKey(style), { start: 0, end: 8 }),
        frameRate: 8,
        repeat: -1,
      });
    }
  });
};

const playHumanAnimation = (visual: HumanVisual, state: HumanAnimationState): void => {
  visual.base.play(getBaseAnimationKey(state), true);
  visual.hair.play(getHairAnimationKey(visual.hairStyle, state), true);
};

export const createHumanVisual = (
  scene: Phaser.Scene,
  x: number,
  y: number,
  options: CreateHumanVisualOptions = {},
): HumanVisual => {
  const { interactive = false, hairStyle = 'shorthair', hairColor, baseColor } = options;
  const visualScale = Math.max(options.scale ?? HUMAN_VISUAL_SCALE, 0.1);

  ensureHumanAnimations(scene);

  const root = scene.add.container(x, y);
  root.setSize(16 * visualScale, 18 * visualScale);

  const base = scene.add
    .sprite(0, 0, HUMAN_BASE_IDLE_TEXTURE_KEY, 0)
    .setOrigin(0.5, 0.6)
    .setScale(visualScale);
  const hair = scene.add
    .sprite(0, 0, getHairTextureKey(hairStyle, 'idle'), 0)
    .setOrigin(0.5, 0.6)
    .setScale(visualScale);
  root.add([base, hair]);

  if (interactive) {
    const hitWidth = 16 * visualScale;
    const hitHeight = 20 * visualScale;
    root.setInteractive(
      new Phaser.Geom.Rectangle(-hitWidth / 2, -hitHeight / 2, hitWidth, hitHeight),
      (shape, hitX, hitY) =>
        Phaser.Geom.Rectangle.Contains(shape as Phaser.Geom.Rectangle, hitX, hitY),
    );
  }

  const visual: HumanVisual = { root, base, hair, state: 'idle', hairStyle, scale: visualScale };
  playHumanAnimation(visual, 'idle');
  setHumanHairTint(visual, hairColor ?? null);
  setHumanBaseTint(visual, baseColor ?? null);

  return visual;
};

export const setHumanVisualState = (visual: HumanVisual, state: HumanAnimationState): void => {
  if (visual.state === state) {
    return;
  }

  playHumanAnimation(visual, state);
  visual.state = state;
};

export const setHumanHairTint = (visual: HumanVisual, hairColor: string | null): void => {
  const tint = toTint(hairColor);
  if (tint === null) {
    visual.hair.clearTint();
    return;
  }

  visual.hair.setTint(tint);
};

export const setHumanBaseTint = (visual: HumanVisual, baseColor: string | null): void => {
  const tint = toTint(baseColor);
  if (tint === null) {
    visual.base.clearTint();
    return;
  }

  visual.base.setTint(blendWithWhite(tint, 0.65));
};
